import { useContext, useEffect } from "react";
import { Col, Form, Row } from "react-bootstrap";
import CreateOrderContext from "../../context/createOrder/";
import { Material } from "../../types/orderTypes";
import InputField from "../forms/Field";
import ColorSelector from "../selectors/ColorSelector";
import { usePrices } from "../../context/prices/PricesContext";
import RegelBedrag from "./RegelBedrag";

// Voorvoegsel voor een zelf omschreven PVC-kleur. De dealer ziet alleen zijn
// eigen tekst; het voorvoegsel is het signaal waarop de backend de
// ColorOption-controle overslaat.
// ⚠️ Moet gelijk blijven aan CUSTOM_COLOR_PREFIX in de backend
// (src/helpers/validateOrderColors.ts).
const ANDERS_PREFIX = "ANDERS: ";
const ANDERS_MAX_LENGTH = 100;

const StairSteps = () => {
  const {
    stairMaterial,
    stairAmount,
    stairColor,
    pvcCustomColor,
  } = useContext(CreateOrderContext);
  // materials komt uit de DB (PriceTable) i.p.v. het hardcoded enum, zodat
  // alleen echt bestaande materialen kiesbaar zijn.
  const { getColors, getMaterialName, getColorImage, materials } = usePrices();

  const baseColors = getColors(stairMaterial.value);
  // Add "Anders" as last option for PVC
  const colors = stairMaterial.value === "PVC"
    ? [...baseColors, "Anders"]
    : baseColors;

  const stairColorImg = getColorImage(stairMaterial.value, stairColor.value);

  // Reset color and custom mode when material changes
  useEffect(() => {
    pvcCustomColor.set(false);
    if (baseColors.length > 0) {
      stairColor.set(baseColors[0] as any);
    }
    // eslint-disable-next-line
  }, [stairMaterial.value]);

  // De kleuren komen pas binnen als /prices geladen is, ná de eerste render.
  // Het effect hierboven heeft dan al met een lege lijst gedraaid; zonder dit
  // effect blijft stairColor leeg en weigert de backend elke calculatie.
  useEffect(() => {
    if (!stairColor.value && baseColors.length > 0) {
      stairColor.set(baseColors[0]);
    }
    // eslint-disable-next-line
  }, [baseColors.length]);

  return (
    <>
      <Row>
        <Form.Group as={Col} xs={6} md={3}>
          <Form.Label>Materiaal Trede</Form.Label>

          <select
            className="select"
            style={{ padding: "8px 4px", position: "inherit" }}
            value={stairMaterial.value}
            onChange={(event) => {
              stairMaterial.set(event.target.value as Material);
            }}
          >
            {materials.map((item: any) => (
              <option value={item} key={item} className="select-item">
                {getMaterialName(item)}
              </option>
            ))}
          </select>
        </Form.Group>

        {pvcCustomColor.value ? (
          <Form.Group as={Col} xs={6} md={3}>
            <Form.Label>Omschrijf de kleur</Form.Label>
            <Form.Control
              type="text"
              value={
                stairColor.value.startsWith(ANDERS_PREFIX)
                  ? stairColor.value.slice(ANDERS_PREFIX.length)
                  : stairColor.value
              }
              placeholder="Omschrijf de kleur"
              maxLength={ANDERS_MAX_LENGTH}
              onChange={(e) => stairColor.set(ANDERS_PREFIX + e.target.value)}
              style={{ padding: "8px 4px" }}
            />
          </Form.Group>
        ) : colors.length > 0 && (
          <ColorSelector
            label="Kleur"
            parse={(value) => {
              if (value === "Anders") {
                pvcCustomColor.set(true);
                // Alleen het voorvoegsel: de omschrijving volgt uit het
                // tekstveld. Blijft die leeg, dan weigert de backend de order.
                return ANDERS_PREFIX as any;
              }
              return value;
            }}
            set={stairColor.set}
            value={stairColor.value as string}
            colors={colors}
            placeholder="Kies een kleur"
          />
        )}

        <InputField
          label="Aantal"
          set={stairAmount.set}
          sizes={{ xs: 6, md: 3 }}
          value={stairAmount.value}
          parse={Number}
        />

        {/* De trap: profielen, treden en (dicht) deelfineer, over alle maten. */}
        <RegelBedrag code={["profielen", "treden", "deelfineer"]} />
      </Row>

      {stairColorImg && (
        <Row className="mb-3">
          <Col xs={6} md={3}>
            <img
              src={stairColorImg}
              alt={String(stairColor.value)}
              style={{
                maxHeight: 70,
                maxWidth: "100%",
                objectFit: "contain",
                border: "1px solid #e2e8f0",
                borderRadius: 4,
                padding: 2,
              }}
            />
          </Col>
        </Row>
      )}
    </>
  );
};

export default StairSteps;
